// Task 7.5: Professional Landing Page (Client Shell)

'use client';

/**
 * Client-side shell for the marketing landing page.
 *
 * This component contains all interactive behaviour (analytics,
 * scroll handlers, hero variant) and receives an optional
 * `initialCurrency` derived from middleware headers so that
 * pricing defaults can be aligned with the detected market.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { HeroSection } from '@/components/landing/HeroSection';
import { ThreeMomentStory } from '@/components/landing/ThreeMomentStory';
import { ProblemSolution } from '@/components/landing/ProblemSolution';
import { PricingTable } from '@/components/landing/PricingTable';
import { EarlyAccessSection } from '@/components/landing/EarlyAccessSection';
import { isFoundingPartnerPhase } from '@/lib/feature-flags/launch-phase';
import { FAQ } from '@/components/landing/FAQ';
import { SecurityCompliance } from '@/components/landing/SecurityCompliance';
import { StructuredData, type FaqItem } from '@/components/seo/StructuredData';
import { MarketingHeader } from '@/components/marketing/marketing-header';
import { analytics, track } from '@/lib/analytics';
import { HowItWorksSection } from '@/components/landing/HowItWorksSection';
import { DemoWorkspaceSection } from '@/components/landing/demo-workspace-section';
import { Button } from '@/components/ui/button';
import { MobileStickyCta } from '@/components/landing/MobileStickyCta';
import { HashFragmentHandler } from '@/components/auth/HashFragmentHandler';
import { PersonaBand } from '@/components/landing/PersonaBand';
import { DifferentiatorsSection } from '@/components/landing/DifferentiatorsSection';
import { PreAuthImportSection } from '@/components/landing/PreAuthImportSection';
import { PreAuthPreviewSection } from '@/components/landing/PreAuthPreviewSection';
import { PreAuthReturnBanner } from '@/components/landing/PreAuthReturnBanner';
import { ImportMappingDialog } from '@/components/instruments/import-mapping-dialog';
import {
  getPreAuthImport,
  savePreAuthImport,
  clearPreAuthImport,
  getTimeRemaining,
  generateFileHash,
  calculatePreAuthStats,
  type PreAuthData,
  type PreAuthInstrument,
} from '@/lib/pre-auth/storage';
import {
  buildInitialSelections,
  createEmptySelections,
  loadStoredMapping,
  parseTabularFile,
  persistMapping,
  selectionsToColumnMapping,
  shouldOpenMappingDialog,
  REQUIRED_MAPPING_FIELDS,
  type AutoAssignmentMap,
  type MappingSelections,
} from '@/components/instruments/import-mapping-utils';
import { captureError, captureMessage, buildTags, toSerializable } from '@/lib/monitoring/logger';
import { toast } from 'sonner';

interface LandingPageClientProps {
  /**
   * Currency inferred from the marketing middleware.
   * Expected values: 'GBP' | 'EUR' | undefined.
   * When undefined, PricingTable falls back to locale-based
   * defaults and existing preference logic.
   */
  initialCurrency?: string;
  cspNonce?: string;
}

const PREAUTH_MAPPING_STORAGE_KEY = 'scopax:preAuthMapping';
const SAMPLE_LIMIT = 3;

function buildSampleValues(
  headers: string[],
  rows: Array<Record<string, string | null>>,
): Record<string, string[]> {
  const samples: Record<string, string[]> = {};
  headers.forEach((header) => {
    samples[header] = [];
  });

  rows.forEach((row) => {
    headers.forEach((header) => {
      const value = row[header];
      if (!value) return;
      const bucket = samples[header];
      if (!bucket.includes(value)) {
        bucket.push(value);
      }
    });
  });

  headers.forEach((header) => {
    samples[header] = samples[header].slice(0, SAMPLE_LIMIT);
  });

  return samples;
}

function getRowValue(
  row: Record<string, string | null>,
  header: string | null | undefined,
): string | null {
  if (!header) return null;
  const value = row[header];
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function mapRowsToPreAuthInstruments(
  rows: Array<Record<string, string | null>>,
  selections: MappingSelections,
): PreAuthInstrument[] {
  return rows.reduce<PreAuthInstrument[]>((acc, row, index) => {
    const rowIndex = index + 1;
    const assetId = getRowValue(row, selections.assetId) ?? `IMPORT-${rowIndex}`;
    const name = getRowValue(row, selections.name) ?? `Instrument ${rowIndex}`;

    if (!assetId && !name) {
      return acc;
    }

    acc.push({
      assetId,
      name,
      lastCalibration: getRowValue(row, selections.lastCalibrationDate),
      nextDue: getRowValue(row, selections.nextCalibrationDue),
      location: getRowValue(row, selections.location),
      manufacturer: getRowValue(row, selections.manufacturer),
      model: getRowValue(row, selections.model),
      serialNumber: getRowValue(row, selections.serialNumber),
    });
    return acc;
  }, []);
}

export function LandingPageClient({ initialCurrency, cspNonce }: LandingPageClientProps) {
  const router = useRouter();
  const t = useTranslations('marketing.faq');
  const rawFaqItems = (t as unknown as { raw?: (key: string) => unknown }).raw?.('items');
  const faqItems = (rawFaqItems as FaqItem[] | undefined) ?? [];
  const [heroVariant, setHeroVariant] = useState<'A' | 'B'>('B');
  const [preAuthData, setPreAuthData] = useState<PreAuthData | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [showReturnBanner, setShowReturnBanner] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [mappingHeaders, setMappingHeaders] = useState<string[]>([]);
  const [mappingSamples, setMappingSamples] = useState<Record<string, string[]>>({});
  const [mappingSelections, setMappingSelections] = useState<MappingSelections>(createEmptySelections());
  const [mappingAutoAssignments, setMappingAutoAssignments] = useState<AutoAssignmentMap>({});
  const [pendingRows, setPendingRows] = useState<Array<Record<string, string | null>>>([]);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const importSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /**
     * Initialise hero variant and track page view.
     * This is intentionally client-only to respect consent
     * and keep analytics out of the server path.
     */
    const variant = (process.env.NEXT_PUBLIC_HERO_VARIANT as 'A' | 'B') || 'B';
    setHeroVariant(variant);

    analytics.pageView('landing');

    // Check for existing pre-auth data from previous visit
    const existingData = getPreAuthImport();
    if (existingData) {
      setPreAuthData(existingData);
      setShowReturnBanner(true);
      const remainingMs = new Date(existingData.expiresAt).getTime() - Date.now();
      const hoursRemaining = Math.max(0, Math.floor(remainingMs / (1000 * 60 * 60)));
      track({
        event: 'preauth_return_visit',
        props: { instrument_count: existingData.instruments.length, hours_remaining: hoursRemaining },
      });
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const handleView = (id: string, event: string) => {
      const element = document.getElementById(id);
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              track({ event, props: { page: 'landing' } });
              obs.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.25 }
      );

      observer.observe(element);
    };

    handleView('demo-workspace', 'view_demo_section');
    handleView('pricing', 'view_pricing_section');
  }, []);

  const founding = isFoundingPartnerPhase();

  const handlePrimaryCtaClick = () => {
    if (founding) {
      router.push('/contact?subject=early-access');
      return;
    }
    const pricingSection = document.getElementById('pricing');
    pricingSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToImportSection = useCallback(() => {
    importSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const finalizePreAuthImport = useCallback(({
    selections,
    rows,
    file,
    autoAssignments,
  }: {
    selections: MappingSelections;
    rows: Array<Record<string, string | null>>;
    file: File;
    autoAssignments: AutoAssignmentMap;
  }) => {
    const instruments = mapRowsToPreAuthInstruments(rows, selections);
    if (instruments.length === 0) {
      throw new Error('No instruments found in file');
    }

    const fileHashSeed = `${file.name}:${file.size}:${file.lastModified}`;
    const fileHash = generateFileHash(fileHashSeed);
    const columnMapping = selectionsToColumnMapping(selections);
    const data: Omit<PreAuthData, 'expiresAt' | 'version'> = {
      instruments,
      columnMapping,
      fileName: file.name,
      fileHash,
      importedAt: new Date().toISOString(),
    };

    savePreAuthImport(data);
    setPreAuthData(getPreAuthImport());
    setShowReturnBanner(false);

    const assignedCount = Object.values(selections).filter(Boolean).length;
    const autoMatched = Object.entries(autoAssignments).filter(([field, assignment]) => {
      if (!assignment) return false;
      const selected = selections[field as keyof MappingSelections];
      return selected === assignment.header;
    }).length;
    const manualMatched = Math.max(0, assignedCount - autoMatched);
    track({
      event: 'preauth_mapping_confirmed',
      props: { columns_auto: autoMatched, columns_manual: manualMatched },
    });

    const stats = calculatePreAuthStats(instruments);
    track({
      event: 'preauth_preview_shown',
      props: {
        instrument_count: instruments.length,
        overdue_count: stats.overdue,
        readiness_score: stats.readinessScore,
      },
    });
  }, []);

  const handleMappingConfirm = useCallback(
    (selections: MappingSelections, rememberSelection: boolean) => {
      if (!pendingFile) {
        setMappingDialogOpen(false);
        return;
      }

      try {
        if (rememberSelection) {
          persistMapping(PREAUTH_MAPPING_STORAGE_KEY, selections, true);
        } else {
          persistMapping(PREAUTH_MAPPING_STORAGE_KEY, selections, false);
        }

        setMappingSelections(selections);
        finalizePreAuthImport({
          selections,
          rows: pendingRows,
          file: pendingFile,
          autoAssignments: mappingAutoAssignments,
        });
        setMappingDialogOpen(false);
      } catch (error) {
        void captureError(error, {
          tags: buildTags('landing', 'preauth-mapping'),
          extra: toSerializable({ fileName: pendingFile.name }),
        });
        toast.error('We could not process that file. Please check the format and try again.');
      }
    },
    [finalizePreAuthImport, mappingAutoAssignments, pendingFile, pendingRows],
  );

  const handleMappingCancel = useCallback(() => {
    setMappingDialogOpen(false);
    setPendingFile(null);
    setPendingRows([]);
    setMappingHeaders([]);
    setMappingSamples({});
    setMappingSelections(createEmptySelections());
    setMappingAutoAssignments({});
  }, []);

  const handleFileSelect = useCallback(async (file: File) => {
    setIsProcessingFile(true);
    track({ event: 'preauth_import_start', props: { file_type: file.type, file_size: file.size } });

    try {
      const storedMapping = loadStoredMapping(PREAUTH_MAPPING_STORAGE_KEY);
      const { headers, rows } = await parseTabularFile(file);
      const samples = buildSampleValues(headers, rows);
      const { selections, autoAssignments } = buildInitialSelections(headers, storedMapping ?? undefined);

      setMappingHeaders(headers);
      setMappingSamples(samples);
      setMappingSelections(selections);
      setMappingAutoAssignments(autoAssignments);
      setPendingRows(rows);
      setPendingFile(file);

      const missingRequired = REQUIRED_MAPPING_FIELDS.filter((field) => !selections[field]).length;
      const shouldOpenDialog = shouldOpenMappingDialog({ autoOpenOnMissing: true }, missingRequired);

      if (shouldOpenDialog) {
        track({ event: 'preauth_mapping_shown', props: { columns_detected: headers.length } });
        setMappingDialogOpen(true);
        return;
      }

      finalizePreAuthImport({
        selections,
        rows,
        file,
        autoAssignments,
      });
    } catch (error) {
      void captureError(error, {
        tags: buildTags('landing', 'preauth-import'),
        extra: toSerializable({ fileName: file.name, fileSize: file.size }),
      });
      toast.error('We could not read that file. Please check the format and try again.');
    } finally {
      setIsProcessingFile(false);
    }
  }, [finalizePreAuthImport]);

  const handleClearPreAuthData = useCallback(() => {
    clearPreAuthImport();
    setPreAuthData(null);
    setShowReturnBanner(false);
    setPendingFile(null);
    setPendingRows([]);
    track({ event: 'preauth_expired', props: { was_returning: false } });
  }, []);

  const handleSignUpWithData = useCallback(() => {
    // Navigate to signup - data persists in localStorage
    track({ event: 'preauth_signup_click', props: { instrument_count: preAuthData?.instruments.length ?? 0 } });
    router.push('/auth/signup');
  }, [router, preAuthData]);

  const handleReviewMapping = useCallback(() => {
    if (!pendingFile) {
      void captureMessage('Pre-auth mapping review requested without a pending file', 'warning', {
        tags: buildTags('landing', 'preauth-mapping'),
      });
      return;
    }
    track({ event: 'preauth_mapping_shown', props: { columns_detected: mappingHeaders.length } });
    setMappingDialogOpen(true);
  }, [mappingHeaders.length, pendingFile]);

  const handleDismissReturnBanner = useCallback(() => {
    setShowReturnBanner(false);
  }, []);

  const handleResumeFromBanner = useCallback(() => {
    setShowReturnBanner(false);
    scrollToImportSection();
  }, [scrollToImportSection]);

  return (
    <>
      {/* Handle auth tokens in URL hash (email confirmation redirects) */}
      <HashFragmentHandler />

      {/* SEO Structured Data */}
      <StructuredData type="organization" nonce={cspNonce} />
      <StructuredData type="software" nonce={cspNonce} />
      <StructuredData type="faq" faqItems={faqItems} nonce={cspNonce} />

      {/* Marketing Header (language switcher gated internally) */}
      <MarketingHeader />

      {/* Pre-Auth Return Banner for returning visitors */}
      {showReturnBanner && preAuthData && (
        <PreAuthReturnBanner
          instrumentCount={preAuthData.instruments.length}
          fileName={preAuthData.fileName}
          timeRemaining={getTimeRemaining()}
          onResume={handleResumeFromBanner}
          onDismiss={handleDismissReturnBanner}
        />
      )}

      <main className="min-h-screen">
        <MobileStickyCta />

        {/* 1. Hero Section */}
        <HeroSection
          variant={heroVariant}
          onCtaClick={handlePrimaryCtaClick}
          onTryWithData={scrollToImportSection}
        />

        {/* ICP Band */}
        <PersonaBand />

        {/* 2. Differentiators */}
        <DifferentiatorsSection />

        {/* 2. Problem/Solution – before/after */}
        <ProblemSolution />

        {/* 3. How it works */}
        <HowItWorksSection
          onVideoClick={() => {
            router.push('/demo-workspace');
          }}
        />

        {/* Pre-Auth Import Section */}
        <section
          id="pre-auth-import"
          ref={importSectionRef}
          className="py-16 bg-muted/30"
        >
          <div className="container mx-auto px-4">
            {preAuthData && preAuthData.instruments.length > 0 ? (
              <PreAuthPreviewSection
                instruments={preAuthData.instruments}
                fileName={preAuthData.fileName}
                timeRemaining={getTimeRemaining()}
                onClear={handleClearPreAuthData}
                onSignUp={handleSignUpWithData}
                onReviewMapping={pendingFile ? handleReviewMapping : undefined}
              />
            ) : (
              <PreAuthImportSection
                onFileSelect={handleFileSelect}
                isProcessing={isProcessingFile}
              />
            )}
          </div>
        </section>

        {/* 4. See it in action (single visual) */}
        <ThreeMomentStory />

        {/* 5. Demo workspace */}
        <DemoWorkspaceSection />

        {/* 6. PRICING / EARLY ACCESS */}
        <div id="pricing">
          {founding ? <EarlyAccessSection /> : <PricingTable initialCurrency={initialCurrency} />}
        </div>

        {/* 7. Security & Compliance */}
        <SecurityCompliance />

        {/* 8. FAQ */}
        <FAQ />

        {/* Final CTA Section */}
        <section className="py-24 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to create your first audit pack?
            </h2>
            <p className="text-xl mb-12 opacity-90">
              {founding
                ? 'Join our founding partner program and get priority onboarding.'
                : 'Go live in 48 hours or your money back.'}
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-3 max-w-2xl mx-auto">
              <Button
                className="w-full md:w-auto px-6 py-4 font-semibold bg-white text-primary hover:bg-white/90 shadow-lg"
                onClick={handlePrimaryCtaClick}
              >
                {founding ? 'Request Access' : 'Buy Starter'}
              </Button>
              <Button
                variant="outline"
                className="w-full md:w-auto px-6 py-4 font-semibold border-2 border-white bg-transparent text-white hover:bg-white/10"
                onClick={() => {
                  router.push('/demo-workspace');
                }}
              >
                Open Demo Workspace
              </Button>
            </div>

            <p className="mt-4 text-sm opacity-90">
              {founding
                ? 'Currently onboarding 5 founding partner teams'
                : '30-day money-back guarantee • Loads sample data • No signup'}
            </p>
          </div>
        </section>
      </main>

      <ImportMappingDialog
        open={mappingDialogOpen}
        headers={mappingHeaders}
        samples={mappingSamples}
        initialSelections={mappingSelections}
        autoAssignments={mappingAutoAssignments}
        onCancel={handleMappingCancel}
        onConfirm={handleMappingConfirm}
      />
    </>
  );
}
